// components/reports/ScoreDial.js
// Real Proove report PDFs render each score as a dynamically-selected raster
// image (a photographed/rendered dial gauge chosen per score band) — this
// app's AI-derived extraction has no such image asset to pick from. This SVG
// component is the closest same-structure substitute: a genuine speedometer-
// style dial (three color-coded Low/Moderate/High bands + a needle), drawn
// with plain SVG so it prints cleanly via react-to-print with no external
// image dependency.
import React from "react";
import { levelColor } from "./reportHelpers";

const BANDS = [
  { angles: [180, 120], color: "#22a355" }, // Low
  { angles: [120, 60], color: "#e0a000" },  // Moderate
  { angles: [60, 0], color: "#d13b3b" },    // High
];
const NEEDLE_DEG = [150, 90, 30];

function bandIndexForLevel(level) {
  const l = String(level || "").toLowerCase();
  // Compound phrases ("moderately low"/"moderately high") must be checked
  // BEFORE the bare "moderate"/"low"/"high" checks below, and this order
  // must exactly match levelColor()/levelBg() in reportHelpers.js — those
  // two functions used to disagree on priority (this file checked "high"
  // then "moderate" then "low"; reportHelpers checked "low" first), so a
  // value like "Moderately Low" lit up the amber needle here while the pill
  // underneath it (colored by reportHelpers) showed green. Keep both files
  // in sync if this order ever changes.
  if (l.includes("moderately low") || l.includes("mildly low")) return 0;
  if (l.includes("moderately high") || l.includes("mildly high")) return 2;
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative")) return 2;
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution")) return 1;
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition")) return 0;
  // COMT haplotype/diplotype codes (e.g. "HPS", "APS/LPS", "LPS/LPS") don't
  // contain the words "high"/"moderate"/"low" at all, so they fell through
  // to null above — a flat grey needle with every band dimmed. Pick the
  // most severe allele present in the code (checked worst-first, since a
  // combined diplotype like "APS/LPS" should read as its riskier half).
  if (l.includes("hps")) return 2;
  if (l.includes("aps")) return 1;
  if (l.includes("lps")) return 0;
  return null;
}

function polarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

const STROKE_WIDTH = 7;
// Rounded stroke caps extend strokeWidth/2 beyond the path's own radius at
// the arc's very top — without this, cy sat only ~0.5px below the SVG's top
// edge, so the cap got clipped by some PDF rasterizers (reported as the
// dial looking "cut in half"). This guarantees real clearance regardless of
// size.
const TOP_PADDING = STROKE_WIDTH;

export default function ScoreDial({ level, score, size = 92 }) {
  const idx = bandIndexForLevel(level);
  const cx = size / 2;
  const r = size / 2 - 10;
  const cy = TOP_PADDING + r;
  const needleColor = idx == null ? "#9a9a9a" : BANDS[idx].color;
  const needleDeg = idx == null ? 90 : NEEDLE_DEG[idx];
  const [nx, ny] = polarToXY(cx, cy, r - 10, needleDeg);
  // Score number sits well clear of the arc, below the needle pivot — never
  // overlapping the gauge itself, regardless of which band the needle
  // points at (the arc only ever occupies the region above cy).
  const numberY = cy + 24;
  // "hanging" baseline means the glyph extends DOWNWARD from numberY by
  // close to the full font height, not just a few px — a fontSize={16} bold
  // number needs ~22px of clearance below numberY, not 8, or its bottom gets
  // clipped by the viewBox boundary (reported as the score number looking
  // cut off/broken).
  const totalHeight = numberY + 22;

  return (
    <svg width={size} height={totalHeight} viewBox={`0 0 ${size} ${totalHeight}`} role="img"
      aria-label={level ? `${level} risk dial` : "risk dial"}>
      {BANDS.map((band, i) => {
        const [x0, y0] = polarToXY(cx, cy, r, band.angles[0]);
        const [x1, y1] = polarToXY(cx, cy, r, band.angles[1]);
        return (
          <path key={i} d={`M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`}
            stroke={band.color} strokeWidth={STROKE_WIDTH} strokeLinecap="round" fill="none"
            opacity={idx === i ? 1 : 0.55} />
        );
      })}
      <circle cx={cx} cy={cy} r={3} fill="#575555" />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={needleColor} strokeWidth={2.5} strokeLinecap="round" />
      {score != null && (
        <text x={cx} y={numberY} textAnchor="middle" dominantBaseline="hanging"
          fontSize={16} fontWeight={800} fill={levelColor(level)}>
          {score}
        </text>
      )}
    </svg>
  );
}
